import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { suppliersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Truck, Plus, Search, Edit2, Trash2, Loader2, Printer } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { CountrySelect } from '../components/shared/CountrySelect';
import { AppPhoneInput } from '../components/shared/PhoneInput';

const emptySupplier = {
    name: '', contact_person: '', email: '', phone: '', address: '',
    country: 'FR',                        // ISO 3166-1 alpha-2 — France by default
    siret: '', tva_number: '', website: '', notes: '',
};

const Suppliers = () => {
    const { t } = useTranslation(['entities', 'common']);
    const { isAdmin } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptySupplier);
    const [saving, setSaving] = useState(false);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const { data } = await suppliersAPI.getAll(search ? { search } : {});
            setItems(data);
        } catch {
            toast.error(t('common:toasts.load_failed'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchItems(); }, []);
    useEffect(() => {
        const t = setTimeout(fetchItems, 300);
        return () => clearTimeout(t);
    }, [search]);

    const openCreate = () => { setEditingId(null); setForm(emptySupplier); setOpen(true); };
    const openEdit = (s) => { setEditingId(s.id); setForm({ ...emptySupplier, ...s }); setOpen(true); };

    const handleSave = async () => {
        if (!form.name?.trim()) { toast.error(t('entities:suppliers.dialog.name_required')); return; }
        setSaving(true);
        try {
            if (editingId) {
                await suppliersAPI.update(editingId, form);
                toast.success(t('entities:suppliers.toasts.updated'));
            } else {
                await suppliersAPI.create(form);
                toast.success(t('entities:suppliers.toasts.created'));
            }
            setOpen(false);
            fetchItems();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common:toasts.save_failed'));
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('common:actions.delete') + '?')) return;
        try {
            await suppliersAPI.delete(id);
            toast.success(t('entities:suppliers.toasts.deleted'));
            fetchItems();
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common:toasts.delete_failed'));
        }
    };

    /**
     * Download + open the branded supplier-card PDF in a new tab. We can't
     * link directly to the protected /api endpoint because the browser
     * won't carry our JWT, so we fetch as a blob via axios and open the
     * resulting Object URL.
     */
    const handlePrintCard = async (s) => {
        try {
            const { data } = await suppliersAPI.cardPdf(s.id);
            const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
            window.open(url, '_blank');
            // Revoke a beat later so the new tab has time to load the blob.
            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common:toasts.load_failed'));
        }
    };

    return (
        <div data-testid="suppliers-page" className="space-y-8 animate-fadeIn">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-[11px] uppercase tracking-[2px] text-[#C08478] font-semibold mb-2">{t('entities:suppliers.section_label')}</p>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">{t('entities:suppliers.title')}</h1>
                    <p className="text-gray-500 mt-1">{t('entities:suppliers.subtitle')}</p>
                </div>
                <Button onClick={openCreate} data-testid="new-supplier-btn" className="bg-[#1A1A1A] hover:bg-[#C08478] text-white">
                    <Plus size={16} className="mr-2" /> {t('entities:suppliers.add')}
                </Button>
            </div>

            <Card className="border-0 shadow-sm">
                <CardHeader className="flex flex-row justify-between items-center">
                    <CardTitle className="flex items-center gap-2">
                        <Truck className="text-[#C08478]" size={20} />
                        {t('entities:suppliers.all')} ({items.length})
                    </CardTitle>
                    <div className="relative w-full max-w-xs">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                            data-testid="supplier-search"
                            placeholder={t('entities:suppliers.search_placeholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 bg-[#F5F5F5] border-[#E5E5E5]"
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#C08478]" /></div>
                    ) : items.length === 0 ? (
                        <div className="py-16 text-center text-gray-400">
                            {t('entities:suppliers.empty')}
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('entities:suppliers.columns.name')}</TableHead>
                                    <TableHead>{t('entities:suppliers.columns.contact')}</TableHead>
                                    <TableHead>{t('entities:suppliers.columns.email')}</TableHead>
                                    <TableHead>{t('entities:suppliers.columns.phone')}</TableHead>
                                    <TableHead>{t('entities:suppliers.columns.siret')}</TableHead>
                                    <TableHead className="text-right">{t('common:common.actions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map((s) => (
                                    <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                                        <TableCell className="font-medium text-[#1A1A1A]">{s.name}</TableCell>
                                        <TableCell className="text-sm">{s.contact_person || '-'}</TableCell>
                                        <TableCell className="text-sm">{s.email || '-'}</TableCell>
                                        <TableCell className="font-mono text-sm">{s.phone || '-'}</TableCell>
                                        <TableCell className="font-mono text-xs">{s.siret || '-'}</TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="icon" onClick={() => handlePrintCard(s)} data-testid={`print-supplier-card-${s.id}`}
                                                    title="Imprimer la fiche fournisseur"
                                                    className="text-gray-500 hover:text-[#C08478]">
                                                <Printer size={16} />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`}>
                                                <Edit2 size={16} />
                                            </Button>
                                            {isAdmin() && (
                                                <Button variant="ghost" size="icon" onClick={() => handleDelete(s.id)} data-testid={`delete-supplier-${s.id}`}
                                                        className="text-gray-500 hover:text-red-500">
                                                    <Trash2 size={16} />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="supplier-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingId ? t('entities:suppliers.dialog.edit') : t('entities:suppliers.dialog.new')}</DialogTitle>
                        <DialogDescription>
                            {t('entities:suppliers.dialog.description')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2 md:col-span-2">
                            <Label>{t('entities:suppliers.dialog.fields.name')} *</Label>
                            <Input data-testid="supplier-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </div>
                        <div className="space-y-2"><Label>{t('entities:suppliers.dialog.fields.contact_person')}</Label>
                            <Input data-testid="supplier-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
                        </div>
                        <div className="space-y-2"><Label>{t('entities:suppliers.dialog.fields.email')}</Label>
                            <Input type="email" data-testid="supplier-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </div>
                        <div className="space-y-2"><Label>{t('entities:suppliers.dialog.fields.phone')}</Label>
                            <AppPhoneInput
                                testid="supplier-phone"
                                value={form.phone}
                                onChange={(v) => setForm({ ...form, phone: v })}
                                defaultCountry="FR"
                            />
                        </div>
                        <div className="space-y-2"><Label>{t('entities:suppliers.dialog.fields.siret')}</Label>
                            <Input data-testid="supplier-siret" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} className="font-mono" />
                        </div>
                        <div className="space-y-2"><Label>{t('entities:suppliers.dialog.fields.tva_number', { defaultValue: 'N° TVA' })}</Label>
                            <Input data-testid="supplier-tva" value={form.tva_number} onChange={(e) => setForm({ ...form, tva_number: e.target.value })} className="font-mono" placeholder="FR12345678901" />
                        </div>
                        <div className="space-y-2"><Label>{t('entities:suppliers.dialog.fields.country', { defaultValue: 'Country' })}</Label>
                            <CountrySelect
                                testid="supplier-country"
                                value={form.country || 'FR'}
                                onChange={(iso) => setForm({ ...form, country: iso })}
                                placeholder="Select country"
                                searchPlaceholder="Search country…"
                                emptyLabel="No country found."
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2"><Label>{t('entities:suppliers.dialog.fields.website', { defaultValue: 'Site Web' })}</Label>
                            <Input data-testid="supplier-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://exemple.com" />
                        </div>
                        <div className="space-y-2 md:col-span-2"><Label>{t('entities:suppliers.dialog.fields.address')}</Label>
                            <Textarea data-testid="supplier-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} />
                        </div>
                        <div className="space-y-2 md:col-span-2"><Label>{t('entities:suppliers.dialog.fields.notes')}</Label>
                            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>{t('common:actions.cancel')}</Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="save-supplier"
                                className="bg-[#1A1A1A] hover:bg-[#C08478] text-white">
                            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
                            {editingId ? t('common:actions.update') : t('common:actions.create')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Suppliers;
